import { HttpClient } from '../api/httpclient.service';

export interface GamificationDelta {
  total_points: number;
  level_key: string;
  level_label: string;
  current_streak: number;
  longest_streak: number;
  target_streak_days?: number | null;
  delta: number;
  level_up: boolean;
  level_icon?: string;
  class_median?: number;
}

export interface GroupBenchmarkStatus {
  benchmark_type: 'class' | 'grade';
  title_level_label: string;
  avg_points: number;
  target_level_label: string;
  points_needed_to_next: number;
  level_key: string;
}

export interface LevelStatusItem {
  id: number;
  key: string;
  label: string;
  label_en?: string;
  min_points: number;
  max_points: number;
  icon?: string;
  order_index: number;
  state: 'completed' | 'active' | 'locked';
  progress_percentage: number;
  is_current_level: boolean;
  is_class_level?: boolean;
  points_needed?: number;
  points_needed_to_next?: number;
  target_level_label?: string;
}

export interface GamificationLevelsStatusResponse {
  user_id: number;
  total_points: number;
  current_level_key: string;
  group_status?: GroupBenchmarkStatus | null;
  levels: LevelStatusItem[];
}

class GamificationService extends HttpClient {
  private _activePromises = new Map<string, Promise<any>>();

  private async _deduplicate<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    if (this._activePromises.has(key)) {
      return this._activePromises.get(key);
    }
    const promise = fetcher().finally(() => {
      this._activePromises.delete(key);
    });
    this._activePromises.set(key, promise);
    return promise;
  }

  async setAward(
    actionKey: string,
    context: Record<string, unknown> = {},
    timezone: string
  ): Promise<GamificationDelta> {
    // Note: setAward is generally not deduplicated by key because it might be intentional rapid calls
    // However, for identical calls in the same tick (like StrictMode), we can deduplicate.
    const dedupKey = `award:${actionKey}:${JSON.stringify(context)}`;
    return this._deduplicate(dedupKey, () => 
      this.post('/gamification/set_award', {
        action_key: actionKey,
        context,
        timezone,
      })
    );
  }

  async setAwardAndGetSummary(
    actionKey: string,
    context: Record<string, unknown> = {},
    timezone: string
  ): Promise<{ points: number; percentile: number; cohort_size: number; gamification_state: GamificationDelta }> {
    const dedupKey = `award_summary:${actionKey}:${JSON.stringify(context)}`;
    return this._deduplicate(dedupKey, () => 
      this.post('/gamification/set_award_and_get_summary', {
        action_key: actionKey,
        context,
        timezone,
      })
    );
  }

  async getState(): Promise<GamificationDelta> {
    return this._deduplicate('state', () => this.get('/gamification/state'));
  }

  async getConfig() {
    return this._deduplicate('config', () => this.get('/gamification/config'));
  }

  async getPercentile(): Promise<{ percentile: number; cohort_size: number }> {
    return this._deduplicate('percentile', () => this.get('/gamification/percentile'));
  }

  async getLessonPoints(lessonId: number, phaseIndex?: number): Promise<{ points: number }> {
    const key = `points:${lessonId}:${phaseIndex || 0}`;
    return this._deduplicate(key, () => {
      const params = new URLSearchParams({ lesson_id: lessonId.toString() });
      if (phaseIndex !== undefined) {
        params.append('phase_index', phaseIndex.toString());
      }
      return this.get(`/gamification/lesson_points?${params.toString()}`);
    });
  }

  async getLevelsStatus(): Promise<GamificationLevelsStatusResponse> {
    return this._deduplicate('levelsStatus', () => this.get('/gamification/levels-status'));
  }
}

export const gamificationService = new GamificationService();
